// PM Frontend Functions Library
// These functions are self-contained and use _shared helpers directly (like Hoomie)

import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/index.js';
import { PreventiveMaintenanceModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';

const COLLECTION_NAME = 'preventive_maintenance';

// Frontend function: Get all preventive maintenance
async function getPreventiveMaintenance(db) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME);
    const preventiveMaintenance = docs.map((doc) => {
      try {
        const preventiveMaintenanceItem = new PreventiveMaintenanceModel(doc);
        return { id: doc.$id, model: preventiveMaintenanceItem };
      } catch (modelError) {
        console.error(
          'Error creating PreventiveMaintenanceModel for doc:',
          doc,
          modelError
        );
        return {
          id: doc.$id,
          model: {
            id: doc.id || doc.$id,
            name: doc.name || 'No name',
            description: doc.description || 'No description',
            freq_unit: doc.freq_unit || 'months',
            freq_value: doc.freq_value || 1,
            anchor_date: doc.anchor_date || Date.now(),
            grace_days: doc.grace_days || 0,
            next_due_at: doc.next_due_at || Date.now(),
            active: doc.active !== undefined ? doc.active : true,
            company_id: doc.company_id || '',
            asset_id: doc.asset_id || '',
            default_priority_cat: doc.default_priority_cat || 2,
            default_assigned_to: doc.default_assigned_to || '',
            checklist: doc.checklist || [],
            parts: doc.parts || [],
            created_by: doc.created_by || '',
            updated_by: doc.updated_by || '',
          },
        };
      }
    });
    return createCMMSView({ data: preventiveMaintenance });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get preventive maintenance by ID
async function getPreventiveMaintenanceById(db, preventiveMaintenanceId) {
  try {
    const doc = await getDocumentById(db, COLLECTION_NAME, preventiveMaintenanceId);
    if (!doc) {
      return createCMMSView({ error: 'Preventive maintenance not found' });
    }

    const preventiveMaintenanceItem = new PreventiveMaintenanceModel(doc);
    return createCMMSView({ data: { id: doc.$id, model: preventiveMaintenanceItem } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get preventive maintenance by company
async function getPreventiveMaintenanceByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });

    const preventiveMaintenance = docs.map((doc) => {
      try {
        const preventiveMaintenanceItem = new PreventiveMaintenanceModel(doc);
        return { id: doc.$id, model: preventiveMaintenanceItem };
      } catch (modelError) {
        console.error(
          'Error creating PreventiveMaintenanceModel for doc:',
          doc,
          modelError
        );
        return {
          id: doc.$id,
          model: {
            id: doc.id || doc.$id,
            name: doc.name || 'No name',
            description: doc.description || 'No description',
            freq_unit: doc.freq_unit || 'months',
            freq_value: doc.freq_value || 1,
            anchor_date: doc.anchor_date || Date.now(),
            grace_days: doc.grace_days || 0,
            next_due_at: doc.next_due_at || Date.now(),
            active: doc.active !== undefined ? doc.active : true,
            company_id: doc.company_id || '',
            asset_id: doc.asset_id || '',
            default_priority_cat: doc.default_priority_cat || 2,
            default_assigned_to: doc.default_assigned_to || '',
            checklist: doc.checklist || [],
            parts: doc.parts || [],
            created_by: doc.created_by || '',
            updated_by: doc.updated_by || '',
          },
        };
      }
    });
    return createCMMSView({ data: preventiveMaintenance });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get active preventive maintenance
async function getActivePreventiveMaintenance(db) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'active',
      operator: '==',
      value: true,
    });

    const preventiveMaintenance = docs.map((doc) => {
      try {
        const preventiveMaintenanceItem = new PreventiveMaintenanceModel(doc);
        return { id: doc.$id, model: preventiveMaintenanceItem };
      } catch (modelError) {
        console.error(
          'Error creating PreventiveMaintenanceModel for doc:',
          doc,
          modelError
        );
        return {
          id: doc.$id,
          model: {
            id: doc.id || doc.$id,
            name: doc.name || 'No name',
            description: doc.description || 'No description',
            freq_unit: doc.freq_unit || 'months',
            freq_value: doc.freq_value || 1,
            anchor_date: doc.anchor_date || Date.now(),
            grace_days: doc.grace_days || 0,
            next_due_at: doc.next_due_at || Date.now(),
            active: doc.active !== undefined ? doc.active : true,
            company_id: doc.company_id || '',
            asset_id: doc.asset_id || '',
            default_priority_cat: doc.default_priority_cat || 2,
            default_assigned_to: doc.default_assigned_to || '',
            checklist: doc.checklist || [],
            parts: doc.parts || [],
            created_by: doc.created_by || '',
            updated_by: doc.updated_by || '',
          },
        };
      }
    });
    return createCMMSView({ data: preventiveMaintenance });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get preventive maintenance by asset
async function getPreventiveMaintenanceByAsset(db, assetId, companyId = null) {
  try {
    const whereConditions = [{ key: 'asset_id', operator: '==', value: assetId }];
    if (companyId) {
      whereConditions.push({ key: 'company_id', operator: '==', value: companyId });
    }
    
    const docs = await getCollection(db, COLLECTION_NAME, whereConditions);

    const preventiveMaintenance = docs.map((doc) => {
      try {
        const preventiveMaintenanceItem = new PreventiveMaintenanceModel(doc);
        return { id: doc.$id, model: preventiveMaintenanceItem };
      } catch (modelError) {
        console.error(
          'Error creating PreventiveMaintenanceModel for doc:',
          doc,
          modelError
        );
        return {
          id: doc.$id,
          model: {
            id: doc.id || doc.$id,
            name: doc.name || 'No name',
            description: doc.description || 'No description',
            freq_unit: doc.freq_unit || 'months',
            freq_value: doc.freq_value || 1,
            anchor_date: doc.anchor_date || Date.now(),
            grace_days: doc.grace_days || 0,
            next_due_at: doc.next_due_at || Date.now(),
            active: doc.active !== undefined ? doc.active : true,
            company_id: doc.company_id || '',
            asset_id: doc.asset_id || '',
            default_priority_cat: doc.default_priority_cat || 2,
            default_assigned_to: doc.default_assigned_to || '',
            checklist: doc.checklist || [],
            parts: doc.parts || [],
            created_by: doc.created_by || '',
            updated_by: doc.updated_by || '',
          },
        };
      }
    });
    return createCMMSView({ data: preventiveMaintenance });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Get overdue preventive maintenance
async function getOverduePreventiveMaintenance(db) {
  try {
    const now = Date.now();
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'next_due_at',
      operator: '<',
      value: now,
    });

    const preventiveMaintenance = docs.map((doc) => {
      try {
        const preventiveMaintenanceItem = new PreventiveMaintenanceModel(doc);
        return { id: doc.$id, model: preventiveMaintenanceItem };
      } catch (modelError) {
        console.error(
          'Error creating PreventiveMaintenanceModel for doc:',
          doc,
          modelError
        );
        return {
          id: doc.$id,
          model: {
            id: doc.id || doc.$id,
            name: doc.name || 'No name',
            description: doc.description || 'No description',
            freq_unit: doc.freq_unit || 'months',
            freq_value: doc.freq_value || 1,
            anchor_date: doc.anchor_date || Date.now(),
            grace_days: doc.grace_days || 0,
            next_due_at: doc.next_due_at || Date.now(),
            active: doc.active !== undefined ? doc.active : true,
            company_id: doc.company_id || '',
            asset_id: doc.asset_id || '',
            default_priority_cat: doc.default_priority_cat || 2,
            default_assigned_to: doc.default_assigned_to || '',
            checklist: doc.checklist || [],
            parts: doc.parts || [],
            created_by: doc.created_by || '',
            updated_by: doc.updated_by || '',
          },
        };
      }
    });
    return createCMMSView({ data: preventiveMaintenance });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Create preventive maintenance
async function createPreventiveMaintenance(db, preventiveMaintenanceData) {
  try {
    const preventiveMaintenanceItem = new PreventiveMaintenanceModel(preventiveMaintenanceData);
    const firestoreData = preventiveMaintenanceItem.toFirestore();

    const docRef = await addDocToCollection(db, COLLECTION_NAME, firestoreData);
    return createCMMSView({ data: { id: docRef.id, model: preventiveMaintenanceItem } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Update preventive maintenance
async function updatePreventiveMaintenance(db, preventiveMaintenanceId, updateData) {
  try {
    const preventiveMaintenanceItem = new PreventiveMaintenanceModel(updateData);
    const firestoreData = preventiveMaintenanceItem.toFirestore();

    await updateDocById(db, COLLECTION_NAME, preventiveMaintenanceId, firestoreData);
    return createCMMSView({ data: { id: preventiveMaintenanceId, model: preventiveMaintenanceItem } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Frontend function: Delete preventive maintenance
async function deletePreventiveMaintenance(db, preventiveMaintenanceId) {
  try {
    await deleteDocByCollectionAndId(db, COLLECTION_NAME, preventiveMaintenanceId);
    return createCMMSView({ data: { id: preventiveMaintenanceId, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

export {
  getPreventiveMaintenance,
  getPreventiveMaintenanceById,
  getPreventiveMaintenanceByCompany,
  getActivePreventiveMaintenance,
  getPreventiveMaintenanceByAsset,
  getOverduePreventiveMaintenance,
  createPreventiveMaintenance,
  updatePreventiveMaintenance,
  deletePreventiveMaintenance,
};
